import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import SubscriptionGate from '../../components/ui/SubscriptionGate';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import propertyService from '../../services/propertyService';
import dossierService from '../../services/dossierService';
import { isPaidPlan, normalizePlanSlug, PLAN_SLUGS } from '../../config/plans';
import DocumentCard from './components/DocumentCard';
import MilestoneCard from './components/MilestoneCard';
import DocumentUploadModal from './components/DocumentUploadModal';
import DocumentViewerModal from './components/DocumentViewerModal';
import MilestoneForm from './components/MilestoneForm';

const PropertyDossier = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const [subscriptionTier, setSubscriptionTier] = useState(PLAN_SLUGS.TRIAL);
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentFilters, setDocumentFilters] = useState({ type: '', search: '' });
  
  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneFilters, setMilestoneFilters] = useState({ category: '', status: '' });
  
  // Active tab
  const [activeTab, setActiveTab] = useState('documents');
  
  // Forms
  const [mortgageData, setMortgageData] = useState({});
  const [insuranceData, setInsuranceData] = useState({});
  const [maintenanceData, setMaintenanceData] = useState({});

  useEffect(() => {
    if (userProfile?.subscription_tier) {
      setSubscriptionTier(normalizePlanSlug(userProfile.subscription_tier));
    }
  }, [userProfile?.subscription_tier]);

  // Load property
  useEffect(() => {
    const loadProperty = async () => {
      const propertyId = searchParams?.get('propertyId');
      if (!propertyId || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        const result = await propertyService.getById(propertyId);
        if (result?.success) {
          setProperty(result.data);
        }
      } catch (error) {
        console.error('Error loading property:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [searchParams, user?.id]);

  // Load documents
  useEffect(() => {
    const loadDocuments = async () => {
      if (!property?.id || !user?.id) return;
      if (!isPaidPlan(subscriptionTier)) return;

      setLoadingDocuments(true);
      try {
        const result = await dossierService.getDocuments(property.id, user.id, documentFilters);
        if (result?.success) {
          setDocuments(result.data || []);
        }
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [property?.id, user?.id, documentFilters, subscriptionTier]);

  // Load milestones
  useEffect(() => {
    const loadMilestones = async () => {
      if (!property?.id || !user?.id) return;
      if (!isPaidPlan(subscriptionTier)) return;

      setLoadingMilestones(true);
      try {
        const result = await dossierService.getMilestones(property.id, user.id, milestoneFilters);
        if (result?.success) {
          setMilestones(result.data || []);
        }
      } catch (error) {
        console.error('Error loading milestones:', error);
      } finally {
        setLoadingMilestones(false);
      }
    };

    loadMilestones();
  }, [property?.id, user?.id, milestoneFilters, subscriptionTier]);

  const handleUploadDocument = async (documentData) => {
    if (!property?.id || !user?.id) return;

    try {
      const result = await dossierService.uploadDocument(
        property.id,
        user.id,
        documentData.file,
        {
          type: documentData.type,
          title: documentData.title,
          description: documentData.description,
          tags: documentData.tags
        }
      );

      if (result?.success) {
        setShowDocumentModal(false);
        // Reload documents
        const reloadResult = await dossierService.getDocuments(property.id, user.id, documentFilters);
        if (reloadResult?.success) {
          setDocuments(reloadResult.data || []);
        }
      } else {
        alert(result?.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Erreur lors de l\'upload du document');
    }
  };

  const handleDeleteDocument = async (document) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${document.title}" ?`)) return;

    try {
      const result = await dossierService.deleteDocument(document.id, user?.id);
      if (result?.success) {
        setDocuments(prev => prev.filter(d => d.id !== document.id));
      } else {
        alert(result?.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveMilestone = async (milestoneData) => {
    if (!property?.id || !user?.id) return;

    try {
      let result;
      if (editingMilestone) {
        result = await dossierService.updateMilestone(editingMilestone.id, user.id, milestoneData);
      } else {
        result = await dossierService.createMilestone(property.id, user.id, milestoneData);
      }

      if (result?.success) {
        setShowMilestoneForm(false);
        setEditingMilestone(null);
        // Switch to timeline tab to see the new milestone
        setActiveTab('timeline');
        // Reload milestones
        const reloadResult = await dossierService.getMilestones(property.id, user.id, milestoneFilters);
        if (reloadResult?.success) {
          setMilestones(reloadResult.data || []);
        }
      } else {
        alert(result?.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving milestone:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleMarkMilestoneDone = async (milestone) => {
    try {
      const result = await dossierService.markMilestoneDone(milestone.id, user?.id);
      if (result?.success) {
        setMilestones(prev => prev.map(m => m.id === milestone.id ? { ...m, status: 'DONE' } : m));
      }
    } catch (error) {
      console.error('Error marking milestone done:', error);
    }
  };

  const handleDeleteMilestone = async (milestone) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${milestone.name}" ?`)) return;

    try {
      const result = await dossierService.deleteMilestone(milestone.id, user?.id);
      if (result?.success) {
        setMilestones(prev => prev.filter(m => m.id !== milestone.id));
      }
    } catch (error) {
      console.error('Error deleting milestone:', error);
    }
  };

  const handleSaveMortgage = async () => {
    if (!property?.id || !user?.id) return;
    if (!mortgageData.due_date) {
      alert('Veuillez entrer une date de renouvellement');
      return;
    }

    const milestoneData = {
      category: 'HYPOTHEQUE',
      name: 'Renouvellement hypothécaire',
      due_date: mortgageData.due_date,
      amount: null,
      meta: {
        lender: mortgageData.lender,
        rate: mortgageData.rate,
        term_months: mortgageData.term_months,
        payment: mortgageData.payment,
        balance: mortgageData.balance,
        notes: mortgageData.notes
      }
    };

    await handleSaveMilestone(milestoneData);
  };

  const handleSaveInsurance = async () => {
    if (!property?.id || !user?.id) return;
    if (!insuranceData.due_date) {
      alert('Veuillez entrer une date de renouvellement');
      return;
    }

    const milestoneData = {
      category: 'ASSURANCE',
      name: 'Renouvellement assurance',
      due_date: insuranceData.due_date,
      amount: insuranceData.premium ? parseFloat(insuranceData.premium) : null,
      meta: {
        insurer: insuranceData.insurer,
        policy_number: insuranceData.policy_number,
        coverage_summary: insuranceData.coverage_summary,
        notes: insuranceData.notes
      }
    };

    await handleSaveMilestone(milestoneData);
    // Clear form after save
    setInsuranceData({});
  };

  const handleSaveMaintenance = async () => {
    if (!property?.id || !user?.id) return;
    if (!maintenanceData.install_date) {
      alert('Veuillez entrer une date d\'installation');
      return;
    }

    const lifespan = parseInt(maintenanceData.lifespan_years) || 10;
    const installDate = new Date(maintenanceData.install_date);
    installDate.setFullYear(installDate.getFullYear() + lifespan);
    const replacementDate = installDate.toISOString().split('T')[0];

    const milestoneData = {
      category: 'CHAUFFE_EAU',
      name: 'Remplacement chauffe-eau',
      due_date: replacementDate,
      amount: null,
      meta: {
        install_date: maintenanceData.install_date,
        expected_lifespan_years: lifespan,
        last_service_date: maintenanceData.last_service_date || null,
        notes: maintenanceData.notes
      }
    };

    await handleSaveMilestone(milestoneData);
    // Clear form after save
    setMaintenanceData({});
  };

  const tabs = [
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'timeline', label: 'Échéances', icon: 'Calendar' },
    { id: 'mortgage', label: 'Hypothèque', icon: 'Home' },
    { id: 'insurance', label: 'Assurance', icon: 'Shield' },
    { id: 'maintenance', label: 'Entretien', icon: 'Droplet' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background">
        <NavigationHeader subscriptionTier={subscriptionTier} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background">
        <NavigationHeader subscriptionTier={subscriptionTier} />
        <div style={{ padding: 'var(--spacing-lg)', textAlign: 'center' }}>
          <p>Propriété non trouvée</p>
          <Button onClick={() => navigate('/main-dashboard')}>Retour au tableau de bord</Button>
        </div>
      </div>
    );
  }

  // Check subscription tier
  if (!isPaidPlan(subscriptionTier)) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background">
        <NavigationHeader subscriptionTier={subscriptionTier} />
        <main style={{ padding: 'var(--spacing-lg)', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-xl)',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            textAlign: 'center',
            marginTop: 'var(--spacing-xl)'
          }}>
            <Icon name="Lock" size={64} className="text-muted-foreground mb-4" />
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-sm) 0'
            }}>
              Module Dossier disponible avec Plus
            </h2>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '1rem',
              color: 'var(--color-muted-foreground)',
              margin: '0 0 var(--spacing-lg) 0',
              maxWidth: '500px'
            }}>
              Gérez vos documents, échéances et rappels pour tous vos immeubles. Organisez vos contrats, inspections, et suivez vos renouvellements d'hypothèque et d'assurance.
            </p>
            <Button
              variant="default"
              size="lg"
              iconName="Crown"
              onClick={() => navigate('/subscription-pricing')}
            >
              Passer à Plus
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      <NavigationHeader subscriptionTier={subscriptionTier} />
      <main style={{ padding: 'var(--spacing-lg)', maxWidth: '1400px', margin: '0 auto', paddingTop: 'calc(64px + 2rem)' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <Button
            variant="ghost"
            size="icon"
            iconName="ArrowLeft"
            onClick={() => navigate(`/analysis-results?propertyId=${property.id}`)}
            style={{ marginBottom: 'var(--spacing-md)' }}
          />
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--color-foreground)',
            margin: '0 0 var(--spacing-xs) 0'
          }}>
            Dossier Immeuble
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '1rem',
            color: 'var(--color-muted-foreground)',
            margin: 0
          }}>
            {property.address}{property.city ? `, ${property.city}` : ''}
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 'var(--spacing-xs)',
          borderBottom: '2px solid var(--color-border)',
          marginBottom: 'var(--spacing-lg)',
          overflowX: 'auto'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${activeTab === tab.id ? 'var(--color-primary)' : 'transparent'}`,
                color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all var(--transition-base)'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                margin: 0
              }}>
                Documents
              </h2>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setShowDocumentModal(true)}
              >
                Ajouter un document
              </Button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
              <Select
                placeholder="Tous les types"
                value={documentFilters.type}
                onChange={(e) => setDocumentFilters(prev => ({ ...prev, type: e.target.value }))}
                options={[
                  { value: '', label: 'Tous les types' },
                  { value: 'INSPECTION', label: 'Inspection' },
                  { value: 'NOTAIRE', label: 'Contrat notaire' },
                  { value: 'CERTIFICAT_LOCALISATION', label: 'Certificat localisation' },
                  { value: 'BAIL', label: 'Bail' },
                  { value: 'FACTURE', label: 'Facture' },
                  { value: 'GARANTIE', label: 'Garantie' },
                  { value: 'PERMIS', label: 'Permis' },
                  { value: 'AUTRE', label: 'Autre' }
                ]}
                style={{ minWidth: '200px' }}
              />
              <Input
                placeholder="Rechercher..."
                value={documentFilters.search}
                onChange={(e) => setDocumentFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{ flex: 1, maxWidth: '300px' }}
              />
            </div>

            {/* Documents List */}
            {loadingDocuments ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-xl)' }}>
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : documents.length === 0 ? (
              <div style={{
                padding: 'var(--spacing-xl)',
                textAlign: 'center',
                background: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)'
              }}>
                <Icon name="FileText" size={48} className="text-muted-foreground mb-4" />
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '1rem',
                  color: 'var(--color-muted-foreground)',
                  margin: 0
                }}>
                  Aucun document. Ajoutez votre premier document pour commencer.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 'var(--spacing-md)'
              }}>
                {documents.map(doc => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    onView={(doc) => {
                      setViewingDocument(doc);
                    }}
                    onEdit={(doc) => {
                      // TODO: Implement edit
                      alert('Édition à venir');
                    }}
                    onDelete={handleDeleteDocument}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                margin: 0
              }}>
                Échéances & Rappels
              </h2>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => {
                  setEditingMilestone(null);
                  setShowMilestoneForm(true);
                }}
              >
                Ajouter une échéance
              </Button>
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)', flexWrap: 'wrap' }}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingMilestone({
                    category: 'HYPOTHEQUE',
                    name: 'Renouvellement hypothécaire',
                    due_date: '',
                    amount: null,
                    meta: {}
                  });
                  setShowMilestoneForm(true);
                }}
              >
                Renouvellement hypothécaire
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingMilestone({
                    category: 'ASSURANCE',
                    name: 'Renouvellement assurance',
                    due_date: '',
                    amount: null,
                    meta: {}
                  });
                  setShowMilestoneForm(true);
                }}
              >
                Renouvellement assurance
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingMilestone({
                    category: 'CHAUFFE_EAU',
                    name: 'Remplacement chauffe-eau',
                    due_date: '',
                    amount: null,
                    meta: {}
                  });
                  setShowMilestoneForm(true);
                }}
              >
                Chauffe-eau
              </Button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
              <Select
                placeholder="Toutes les catégories"
                value={milestoneFilters.category}
                onChange={(e) => setMilestoneFilters(prev => ({ ...prev, category: e.target.value }))}
                options={[
                  { value: '', label: 'Toutes les catégories' },
                  { value: 'HYPOTHEQUE', label: 'Hypothèque' },
                  { value: 'ASSURANCE', label: 'Assurance' },
                  { value: 'CHAUFFE_EAU', label: 'Chauffe-eau' },
                  { value: 'CERTIFICAT_LOCALISATION', label: 'Certificat localisation' },
                  { value: 'INSPECTION', label: 'Inspection' },
                  { value: 'AUTRE', label: 'Autre' }
                ]}
                style={{ minWidth: '200px' }}
              />
              <Select
                placeholder="Tous les statuts"
                value={milestoneFilters.status}
                onChange={(e) => setMilestoneFilters(prev => ({ ...prev, status: e.target.value }))}
                options={[
                  { value: '', label: 'Tous les statuts' },
                  { value: 'UPCOMING', label: 'À venir' },
                  { value: 'OVERDUE', label: 'En retard' },
                  { value: 'DONE', label: 'Terminé' }
                ]}
                style={{ minWidth: '200px' }}
              />
            </div>

            {/* Milestones List */}
            {loadingMilestones ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-xl)' }}>
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
              </div>
            ) : milestones.length === 0 ? (
              <div style={{
                padding: 'var(--spacing-xl)',
                textAlign: 'center',
                background: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)'
              }}>
                <Icon name="Calendar" size={48} className="text-muted-foreground mb-4" />
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '1rem',
                  color: 'var(--color-muted-foreground)',
                  margin: 0
                }}>
                  Aucune échéance. Ajoutez votre première échéance pour commencer.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                {milestones.map(milestone => (
                  <MilestoneCard
                    key={milestone.id}
                    milestone={milestone}
                    onEdit={(m) => {
                      setEditingMilestone(m);
                      setShowMilestoneForm(true);
                    }}
                    onMarkDone={handleMarkMilestoneDone}
                    onDelete={handleDeleteMilestone}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'mortgage' && (
          <div style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            maxWidth: '600px'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-lg) 0'
            }}>
              Hypothèque
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <Input
                label="Prêteur"
                value={mortgageData.lender || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, lender: e.target.value }))}
                placeholder="Ex: Banque Nationale"
              />
              <Input
                label="Taux d'intérêt (%)"
                type="number"
                value={mortgageData.rate || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, rate: e.target.value }))}
                placeholder="Ex: 5.5"
              />
              <Input
                label="Terme (mois)"
                type="number"
                value={mortgageData.term_months || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, term_months: e.target.value }))}
                placeholder="Ex: 300"
              />
              <Input
                label="Paiement mensuel ($)"
                type="number"
                value={mortgageData.payment || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, payment: e.target.value }))}
                placeholder="Ex: 1500"
              />
              <Input
                label="Solde actuel ($)"
                type="number"
                value={mortgageData.balance || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, balance: e.target.value }))}
                placeholder="Ex: 250000"
              />
              <Input
                label="Date de renouvellement"
                type="date"
                value={mortgageData.due_date || ''}
                onChange={(e) => setMortgageData(prev => ({ ...prev, due_date: e.target.value }))}
                required
              />
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Notes
                </label>
                <textarea
                  value={mortgageData.notes || ''}
                  onChange={(e) => setMortgageData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes additionnelles..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-background)',
                    color: 'var(--color-foreground)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>
              <Button
                variant="default"
                iconName="Save"
                onClick={handleSaveMortgage}
              >
                Enregistrer et créer l'échéance
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'insurance' && (
          <div style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            maxWidth: '600px'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-lg) 0'
            }}>
              Assurance
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <Input
                label="Assureur"
                value={insuranceData.insurer || ''}
                onChange={(e) => setInsuranceData(prev => ({ ...prev, insurer: e.target.value }))}
                placeholder="Ex: Intact Assurance"
              />
              <Input
                label="Numéro de police"
                value={insuranceData.policy_number || ''}
                onChange={(e) => setInsuranceData(prev => ({ ...prev, policy_number: e.target.value }))}
                placeholder="Ex: POL-123456"
              />
              <Input
                label="Prime annuelle ($)"
                type="number"
                value={insuranceData.premium || ''}
                onChange={(e) => setInsuranceData(prev => ({ ...prev, premium: e.target.value }))}
                placeholder="Ex: 1200"
              />
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Résumé de couverture
                </label>
                <textarea
                  value={insuranceData.coverage_summary || ''}
                  onChange={(e) => setInsuranceData(prev => ({ ...prev, coverage_summary: e.target.value }))}
                  placeholder="Ex: Assurance habitation complète"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-background)',
                    color: 'var(--color-foreground)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    minHeight: '60px'
                  }}
                />
              </div>
              <Input
                label="Date de renouvellement"
                type="date"
                value={insuranceData.due_date || ''}
                onChange={(e) => setInsuranceData(prev => ({ ...prev, due_date: e.target.value }))}
                required
              />
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Notes
                </label>
                <textarea
                  value={insuranceData.notes || ''}
                  onChange={(e) => setInsuranceData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes additionnelles..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-background)',
                    color: 'var(--color-foreground)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <Button
                  variant="default"
                  iconName="Save"
                  onClick={handleSaveInsurance}
                  style={{ flex: 1 }}
                >
                  Enregistrer et créer l'échéance
                </Button>
                <Button
                  variant="outline"
                  iconName="ShoppingCart"
                  onClick={() => alert('Fonctionnalité à venir')}
                >
                  Magasiner
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-lg)',
            maxWidth: '600px'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-lg) 0'
            }}>
              Entretien - Chauffe-eau
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              <Input
                label="Date d'installation"
                type="date"
                value={maintenanceData.install_date || ''}
                onChange={(e) => {
                  setMaintenanceData(prev => ({ ...prev, install_date: e.target.value }));
                  // Auto-calculate replacement date
                  if (e.target.value) {
                    const installDate = new Date(e.target.value);
                    const lifespan = parseInt(maintenanceData.lifespan_years) || 10;
                    installDate.setFullYear(installDate.getFullYear() + lifespan);
                    // This will be used when saving
                  }
                }}
                required
              />
              <Input
                label="Durée de vie estimée (années)"
                type="number"
                value={maintenanceData.lifespan_years || '10'}
                onChange={(e) => setMaintenanceData(prev => ({ ...prev, lifespan_years: e.target.value }))}
                placeholder="10"
              />
              <Input
                label="Date du dernier entretien"
                type="date"
                value={maintenanceData.last_service_date || ''}
                onChange={(e) => setMaintenanceData(prev => ({ ...prev, last_service_date: e.target.value }))}
              />
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                  marginBottom: 'var(--spacing-xs)'
                }}>
                  Notes
                </label>
                <textarea
                  value={maintenanceData.notes || ''}
                  onChange={(e) => setMaintenanceData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes additionnelles..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-background)',
                    color: 'var(--color-foreground)',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    minHeight: '80px'
                  }}
                />
              </div>
              {maintenanceData.install_date && (
                <div style={{
                  padding: 'var(--spacing-sm)',
                  background: 'var(--color-muted)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  color: 'var(--color-foreground)'
                }}>
                  <strong>Date de remplacement estimée:</strong>{' '}
                  {(() => {
                    const installDate = new Date(maintenanceData.install_date);
                    const lifespan = parseInt(maintenanceData.lifespan_years) || 10;
                    installDate.setFullYear(installDate.getFullYear() + lifespan);
                    return installDate.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
                  })()}
                </div>
              )}
              <Button
                variant="default"
                iconName="Save"
                onClick={handleSaveMaintenance}
                disabled={!maintenanceData.install_date}
              >
                Enregistrer et créer l'échéance
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onUpload={handleUploadDocument}
        propertyId={property?.id}
      />

      <DocumentViewerModal
        isOpen={!!viewingDocument}
        onClose={() => setViewingDocument(null)}
        document={viewingDocument}
      />

      <MilestoneForm
        isOpen={showMilestoneForm}
        onClose={() => {
          setShowMilestoneForm(false);
          setEditingMilestone(null);
        }}
        onSave={handleSaveMilestone}
        milestone={editingMilestone}
        property={property}
      />

      {showSubscriptionGate && (
        <SubscriptionGate
          requiredTier="plus"
          featureName="le module Dossier"
          onClose={() => setShowSubscriptionGate(false)}
        />
      )}
    </div>
  );
};

export default PropertyDossier;
